import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePersonalFavicon } from '../../hooks/usePersonalFavicon';
import { useGuruRecorder } from '../../hooks/useGuruRecorder';
import PortalNavbar from './PortalNavbar';
import CriticalAlertBar from './CriticalAlertBar';
import { AppErrorBoundary } from '../common/AppErrorBoundary';
import { RouteErrorBoundary } from '../common/RouteErrorBoundary';
import EmailComposeModal from './EmailComposeModal';
import PortalFAB from './PortalFAB';
import { ImpersonationBanner } from '../layout/ImpersonationBanner';
import {
  isQualityCopilotRoute,
  isTechSupportCopilotRoute,
  isAnalystRoute,
  isKnowledgeBanksRoute,
} from './portalLayoutUtils';
import { isExecPortalUser, seesCriticalAlertBar } from '../../utils/landingPage';
import { portalService } from '../../services/portal';
import { portalOrderService } from '../../services/portalOrders';
import { notificationService } from '../../services/notifications';
import { trackEvent } from '../../services/portalTracking';
import { usePortalPageViews } from '../../hooks/usePortalPageViews';
import styles from './PortalLayout.module.css';

/**
 * Telemetry hook for the portal's error boundaries: a contained render crash
 * is still an outage for the CSR who hit it, so record it in the portal event
 * stream (same buffer as clicks) where the analytics audit can see it.
 */
function reportRenderError(error: Error, _info: unknown, scope: string): void {
  trackEvent('action', 'render_error_boundary', {
    scope,
    message: error.message,
    name: error.name,
  });
}

const PortalLayout: React.FC = () => {
  const { user, hasScope } = useAuth();
  // Per-user tab-icon override (kanban 806e60af) — portal-scoped by living here.
  usePersonalFavicon(user?.email);
  // Shared Guru user-action recorder: screens as route templates, user id +
  // role, pause/scroll summaries (gurucloudai kanban b91c17bf).
  useGuruRecorder();
  // Executive readers (PJ / Ravi) may be ADMIN-role, which would light
  // every CSR affordance via the hasScope short-circuit; their shell carries
  // none of the order-desk polling, badges or banners.
  const isExec = !!user && isExecPortalUser(user);
  const canManageOrders = hasScope('orders:manage') && !isExec;
  // orders:read viewers get the Orders tab (read-only), so its draft-count
  // badge loads for them too; inbox/action-item polling stays manage-only.
  const canViewOrders = hasScope('orders:read') && !isExec;

  const [inboxCount, setInboxCount] = useState(0);
  const [ordersNeedReviewCount, setOrdersNeedReviewCount] = useState(0);
  const [actionItemsTotal, setActionItemsTotal] = useState(0);
  const [freshUnreadCount, setFreshUnreadCount] = useState(0);

  // Controls whether the notification panel should be opened from the banner
  const [openNotificationsRequested, setOpenNotificationsRequested] = useState(false);

  // Compose modal state (new compose from navbar)
  const [showCompose, setShowCompose] = useState(false);
  const [composeAction, setComposeAction] = useState<'reply' | 'reply_all' | 'forward' | 'new'>('new');
  const [composeEmailId, setComposeEmailId] = useState<string | null>(null);

  // --- Interaction tracking ---
  const location = useLocation();
  // The chat-style co-pilot doors get a clean, full-bleed canvas: no
  // must-acknowledge critical bar, and no portal content inset (see .mainFlush).
  // The Quality Co-Pilot (voice), John Kirk's Tech Support Co-Pilot (text), and
  // the Analyst (text) all qualify. (The Inbox Analytics tab is a normal inset
  // page, so it is intentionally NOT in this set.)
  const isQualityCopilot = isQualityCopilotRoute(location.pathname);
  const isTechSupportCopilot = isTechSupportCopilotRoute(location.pathname);
  const isAnalyst = isAnalystRoute(location.pathname);
  // The Knowledge Banks tab is an iframe of the hosted KB Explorer: same
  // edge-to-edge canvas so the embedded page owns its own scrolling.
  const isKnowledgeBanks = isKnowledgeBanksRoute(location.pathname);
  const isFullBleedCopilot = isQualityCopilot || isTechSupportCopilot || isAnalyst || isKnowledgeBanks;

  // One page_view per screen, including the one the layout mounts on
  // (hooks/usePortalPageViews.ts); the old inline effect skipped the landing page.
  usePortalPageViews();

  const fetchStats = useCallback(async () => {
    if (!canManageOrders) return;
    try {
      const stats = await portalService.getInboxStats();
      // Navbar inbox badge mirrors the Important tab pill — both are the
      // "needs your attention right now" signal.  Previously sourced from
      // stats.unread, which surfaced 99+ even when zero items needed action.
      setInboxCount(stats.important);
    } catch (err) {
      console.error('Failed to fetch inbox stats:', err);
    }
  }, [canManageOrders]);

  const fetchOrderStats = useCallback(async () => {
    if (!canViewOrders) return;
    try {
      const stats = await portalOrderService.getStats();
      setOrdersNeedReviewCount(stats.draft_count ?? 0);
    } catch {
      // Silent — orders badge just won't show
    }
  }, [canViewOrders]);

  const fetchActionItems = useCallback(async () => {
    if (!canManageOrders) return;
    try {
      const response = await notificationService.getActionItems();
      setActionItemsTotal(response.total);
    } catch (err) {
      console.error('Failed to fetch action items:', err);
    }
  }, [canManageOrders]);

  // The bell's dot: fresh unread bell-only notifications. Unlike the
  // action-item badge this is NOT scope-gated — it is how bell-only
  // notifications (e.g. Jess's investigation alerts) reach users who
  // never see action items.
  const fetchBellIndicator = useCallback(async () => {
    try {
      setFreshUnreadCount(await notificationService.getBellIndicator());
    } catch {
      // Silent — the dot just won't show.
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchOrderStats();
    fetchActionItems();
    fetchBellIndicator();
    const interval = setInterval(() => {
      fetchStats();
      fetchOrderStats();
      fetchActionItems();
      fetchBellIndicator();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchOrderStats, fetchActionItems, fetchBellIndicator]);

  return (
    <>
      <PortalNavbar
        inboxCount={inboxCount}
        ordersNeedReviewCount={ordersNeedReviewCount}
        actionItemCount={actionItemsTotal}
        freshUnreadCount={freshUnreadCount}
        openNotificationsRequested={openNotificationsRequested}
        onNotificationsOpened={() => setOpenNotificationsRequested(false)}
        onCompose={() => {
          trackEvent('action', 'compose_new');
          setComposeEmailId(null);
          setComposeAction('new');
          setShowCompose(true);
        }}
        onActionItemMutated={() => {
          fetchActionItems();
          fetchBellIndicator();
        }}
      />
      <div
        className={`${styles.layout}${
          isFullBleedCopilot ? ` ${styles.layoutFlush}` : ''
        }`}
      >
        {/* The "Viewing as <user>" notice sits INSIDE the layout — below the
            fixed navbar's 56px offset — so admin "View as" shows a visible strip
            instead of rendering behind the navbar and leaving an unexplained
            white gap. Full-bleed copilot pages shrink to fit it (see .mainFlush),
            so it no longer pushes the page into an overflow scroll. */}
        <ImpersonationBanner />
        {/* The action-items banner that used to sit here was retired: since
            the bell rulings of 2026-09-24/25 (PR #2299 / #2315, card 9ceb1a89)
            no ACTION_ITEM_TYPES row reaches the bell surface, so
            /notifications/action-items always counts 0 and the banner could
            never render (card 9911d87e). The count still feeds the navbar
            bell badge below; bringing a type back into the bell is a change
            to services/notifications/surfaces.py, not here. */}
        <main
          className={`${styles.main}${isFullBleedCopilot ? ` ${styles.mainFlush}` : ''}`}
        >
          {/* Must-acknowledge critical alerts surface prominently at the top
              of the content area and persist until acknowledged. For CSRs and
              for salespeople (a critical alert may be routed to an individual
              salesperson); never for admins, managers or executive readers,
              who are not CSRs (owner ruling 2026-09-28, seesCriticalAlertBar);
              and not on the full-bleed co-pilots, where the user asked for a
              distraction-free surface. */}
          {!isFullBleedCopilot && seesCriticalAlertBar(user) && (
            <AppErrorBoundary scope="section" label="Critical alerts" onError={reportRenderError}>
              <CriticalAlertBar />
            </AppErrorBoundary>
          )}
          {/* A crashing page must not take the navbar / banners with it
              (kanban 90f3b9d8): the boundary resets on navigation so any
              tab click recovers. */}
          <RouteErrorBoundary homeHref="/portal" onError={reportRenderError}>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>
      <PortalFAB />

      {/* Compose modal — new email from navbar compose button */}
      {showCompose && (
        <EmailComposeModal
          emailId={composeEmailId}
          action={composeAction}
          onClose={() => setShowCompose(false)}
          onSent={() => {
            setShowCompose(false);
          }}
        />
      )}
    </>
  );
};

export default PortalLayout;
